"use client";

// Favourite cam models: kept in the browser like the other lists (lib/library.ts), and once signed in
// mirrored to the account (/api/me/cams), what was saved before signing in merged in. The bell
// (components/notifications) says when one is live; browser notifications say so with the site closed.

import { useCallback, useSyncExternalStore } from "react";

export interface CamFavorite {
  source: string;
  /** The room's name at the source (lower case). */
  username: string;
  name: string;
  avatar: string | null;
}

const KEY = "sweet.cams";
const MAX = 200;
const EMPTY: CamFavorite[] = [];
let cache: CamFavorite[] | null = null;
const listeners = new Set<() => void>();
let remote = false;

/** "<source>:<username>": how a favourite is named to the server and the notifier. */
export const camKey = (c: Pick<CamFavorite, "source" | "username">) => `${c.source}:${c.username}`;

function read(): CamFavorite[] {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(KEY);
    cache = raw ? JSON.parse(raw) : EMPTY;
  } catch {
    cache = EMPTY;
  }
  return cache ?? EMPTY;
}

function write(list: CamFavorite[]) {
  cache = list;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Storage blocked: kept for this page only.
  }
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useCamFavorites(): CamFavorite[] {
  return useSyncExternalStore(subscribe, read, () => EMPTY);
}

/** Whether a model is a favourite, and the toggle. */
export function useCamFavorite(model: CamFavorite) {
  const list = useCamFavorites();
  const on = list.some((c) => camKey(c) === camKey(model));
  const toggle = useCallback(() => {
    const current = read();
    const add = !current.some((c) => camKey(c) === camKey(model));
    write(add ? [{ source: model.source, username: model.username, name: model.name, avatar: model.avatar }, ...current].slice(0, MAX) : current.filter((c) => camKey(c) !== camKey(model)));
    if (remote) void fetch("/api/me/cams", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...model, on: add }) }).catch(() => {});
  }, [model]);
  return [on, toggle] as const;
}

/** Adopts the account's favourites once signed in, uploading those saved before (LibrarySync calls it). */
export async function syncCamFavorites() {
  const res = await fetch("/api/me/cams", { cache: "no-store" });
  if (!res.ok) return;
  const server: CamFavorite[] = (await res.json()).items ?? [];
  remote = true;
  const local = read().filter((c) => !server.some((s) => camKey(s) === camKey(c)));
  for (const c of local) await fetch("/api/me/cams", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...c, on: true }) }).catch(() => {});
  write([...local, ...server].slice(0, MAX));
}
